/**
 * TanStack Query client shared by the whole portal.
 *
 * Defaults:
 *  - staleTime 30s: dedupes bursts of useQuery mounts within the same view
 *    without spamming the backend.
 *  - refetchOnWindowFocus: 'always' — returning to the portal tab (e.g. from
 *    the game-client) ALWAYS refetches, ignoring staleTime. Plain `true` only
 *    revalidates queries that are already stale, which would leave the user
 *    looking at <30s-old XP/badges/portfolio after coming back from the game.
 *  - refetchOnReconnect: 'always' — same reasoning when the browser comes
 *    back online.
 */

import { QueryClient } from '@tanstack/react-query';
import { forgetAtSessionEnd } from './session-end';

/**
 * Skip retrying on 4xx responses — those are permission/validation errors that
 * won't fix themselves on retry. Retry once for transient 5xx / network errors.
 *
 * Domain apis throw `ApiError` (with a numeric `status`); we read that directly.
 * The message regex is a fallback for any non-ApiError thrown.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  const msg = error instanceof Error ? error.message : String(error);
  if (/\b(400|401|403|404|409|422)\b/.test(msg)) return false;
  return failureCount < 1;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: 'always',
      refetchOnReconnect: 'always',
      retry: shouldRetry,
    },
    mutations: {
      retry: 0,
    },
  },
});

// A session's end empties the cache: the next user must not see the previous
// one's data on queries keyed only by the cache key (['student-portfolio'],
// ['student-progress']).
forgetAtSessionEnd(() => queryClient.clear());

// Query keys live per-domain in `api/<area>/<domain>/keys.ts`
// (e.g. `studentCampaignsKeys`) — there is no central registry.
// See PORTAL-FRONTEND-ARCHITECTURE.md.
